import { collection, doc, getDoc, getDocs, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import { AppUser, UserRole, UserLanguage } from '../types';

export async function createUserDocument(uid: string, email: string, firstname: string = '', lastname: string = ''): Promise<void> {
  const userRef = doc(db, 'users', uid);
  const userSnap = await getDoc(userRef);
  if (!userSnap.exists()) {
    await setDoc(userRef, {
      uid,
      email,
      firstname,
      lastname,
      role: 'guest' as UserRole,
      language: 'pl' as UserLanguage,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }
}

export async function getUserDocument(uid: string): Promise<AppUser | null> {
  const userRef = doc(db, 'users', uid);
  const userSnap = await getDoc(userRef);
  if (userSnap.exists()) {
    const data = userSnap.data();
    return {
      uid: data.uid,
      email: data.email,
      firstname: data.firstname,
      lastname: data.lastname,
      role: data.role as UserRole,
      language: (data.language as UserLanguage) ?? 'pl',
      createdAt: data.createdAt?.toDate?.() ?? new Date(),
      updatedAt: data.updatedAt?.toDate?.() ?? new Date(),
      lastLoginAt: data.lastLoginAt?.toDate?.(),
    };
  }
  return null;
}

export async function updateLastLogin(uid: string): Promise<void> {
  const userRef = doc(db, 'users', uid);
  await setDoc(userRef, { lastLoginAt: serverTimestamp() }, { merge: true });
}

export async function updateUserRole(uid: string, role: UserRole): Promise<void> {
  const userRef = doc(db, 'users', uid);
  await updateDoc(userRef, { role, updatedAt: serverTimestamp() });
}

export async function updateUserLanguage(uid: string, language: UserLanguage): Promise<void> {
  const userRef = doc(db, 'users', uid);
  await updateDoc(userRef, { language, updatedAt: serverTimestamp() });
}

export async function updateUserProfile(uid: string, firstname: string, lastname: string): Promise<void> {
  const userRef = doc(db, 'users', uid);
  await updateDoc(userRef, { firstname, lastname, updatedAt: serverTimestamp() });
}

export async function getPublicUsers(): Promise<Record<string, { firstname: string; lastname: string }>> {
  const snap = await getDocs(collection(db, 'publicUsers'));
  const result: Record<string, { firstname: string; lastname: string }> = {};
  for (const d of snap.docs) {
    const data = d.data();
    result[d.id] = {
      firstname: (data.firstname as string) ?? '',
      lastname: (data.lastname as string) ?? '',
    };
  }
  return result;
}
